import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Bug, Download, Eye, FileText, FolderInput, FolderPlus, Link2, LucideIcon, Share2, Tags, Trash2 } from 'lucide-react'
import { PHOTO_FOLDERS, UPDATE_PHOTOS } from '../api'
import { photosZipUrl, ZIP_MAX_IDS } from '../lib/bulk'
import { describePhotosError, photosErrorKey } from '../lib/errors'
import { folderLabel, folderLabels } from '../lib/folders'
import { MAX_SHARE_PHOTOS } from '../lib/share'
import type { PhotoUi } from '../lib/permissions'
import type { Photo, PhotoFolder, PhotoVisibility } from '../types'
import { orderedFolders } from './BatchMetadataFields'
import BulkEditDialog from './BulkEditDialog'
import DeletePhotosDialog from './DeletePhotosDialog'

export interface PhotoAction {
  key: string
  label: string
  icon: LucideIcon
  /** Shown as its own button in the selection bar; the rest sit under "more". */
  primary: boolean
  destructive?: boolean
  disabled?: boolean
  /** Why the action is disabled. */
  hint?: string | null
  onSelect?: () => void
  /** A choice to make first (folder, visibility) instead of a direct action. */
  options?: { key: string; label: string; onSelect: () => void }[]
}

export interface PhotoBulkActionsOptions {
  projectId: string
  selectedIds: string[]
  photosById: Map<string, Photo>
  ui: PhotoUi
  onClear: () => void
  onReport?: () => void
  onCreateDefect?: (ids: string[]) => void
  onAttachToSnag?: (ids: string[]) => void
  onAddToAlbum?: (ids: string[]) => void
  onShare?: (ids: string[]) => void
}

/** What can be done with the selected photos — one list for the selection bar and the right-click menu (Freelo #33206294). */
export const usePhotoBulkActions = (o: PhotoBulkActionsOptions): { actions: PhotoAction[]; loading: boolean; dialogs: React.ReactNode } => {
  const { projectId, selectedIds, photosById, ui, onClear } = o
  const { t } = useTranslation()
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const { data: folderData } = useQuery<{ photoFolders: PhotoFolder[] }>(PHOTO_FOLDERS, { variables: { projectId }, skip: !projectId })
  const [updatePhotos, { loading }] = useMutation(UPDATE_PHOTOS, {
    context: { handlesErrors: true },
    refetchQueries: ['photos', 'photoFolders'],
  })

  const photos = selectedIds.map((id) => photosById.get(id)).filter((p): p is Photo => !!p)
  const canChangeVisibility = ui.isAdmin || photos.every((p) => p.uploadedBy.id === ui.userId)
  const zipTooBig = selectedIds.length > ZIP_MAX_IDS
  const shareTooBig = selectedIds.length > MAX_SHARE_PHOTOS

  const run = async (input: { folderId?: string; visibility?: PhotoVisibility }) => {
    try {
      await updatePhotos({ variables: { ids: selectedIds, input } })
      toastr.success('', t('Photos_bulk_updated', { count: selectedIds.length }))
      onClear()
    } catch (e: any) {
      toastr.error(t('Common_error'), describePhotosError(photosErrorKey(e), t))
    }
  }

  const actions: PhotoAction[] = []
  if (ui.canEdit) {
    actions.push({ key: 'edit', label: t('Photos_bulk_edit'), icon: Tags, primary: true, disabled: loading, onSelect: () => setEditOpen(true) })
    actions.push({
      key: 'move',
      label: t('Photos_bulk_move'),
      icon: FolderInput,
      primary: true,
      disabled: loading,
      options: orderedFolders(folderData?.photoFolders ?? []).map(({ folder, depth }) => ({
        key: folder.id,
        label: `${'– '.repeat(depth)}${folderLabel(folder, folderLabels(t))}`,
        onSelect: () => run({ folderId: folder.id }),
      })),
    })
  }
  if (ui.canManageStructure && o.onAddToAlbum) {
    const add = o.onAddToAlbum
    actions.push({ key: 'album', label: t('Photos_bulk_album'), icon: FolderPlus, primary: true, disabled: loading, onSelect: () => add(selectedIds) })
  }
  if (ui.canExport) {
    actions.push({
      key: 'zip',
      label: 'ZIP',
      icon: Download,
      primary: true,
      disabled: zipTooBig,
      hint: zipTooBig ? t('Photos_bulk_zip_too_many', { max: ZIP_MAX_IDS }) : null,
      onSelect: () => window.open(photosZipUrl(projectId, selectedIds)),
    })
  }
  if (o.onCreateDefect) {
    const create = o.onCreateDefect
    actions.push({ key: 'defect', label: t('Photos_bulk_defect'), icon: Bug, primary: true, onSelect: () => create(selectedIds) })
  }
  if (ui.canEdit) {
    actions.push({
      key: 'visibility',
      label: t('Photos_filter_visibility'),
      icon: Eye,
      primary: false,
      disabled: loading || !canChangeVisibility,
      hint: canChangeVisibility ? null : t('Photos_bulk_visibility_own_only'),
      options: [
        { key: 'PROJECT', label: t('Photos_visibility_project'), onSelect: () => run({ visibility: 'PROJECT' }) },
        { key: 'INTERNAL', label: t('Photos_visibility_internal'), onSelect: () => run({ visibility: 'INTERNAL' }) },
      ],
    })
  }
  if (ui.canExport && o.onReport) {
    actions.push({ key: 'report', label: t('Photos_bulk_report'), icon: FileText, primary: false, onSelect: o.onReport })
  }
  if (o.onShare) {
    const share = o.onShare
    actions.push({
      key: 'share',
      label: t('Photos_share'),
      icon: Share2,
      primary: false,
      disabled: shareTooBig,
      hint: shareTooBig ? t('Photos_share_too_many') : null,
      onSelect: () => share(selectedIds),
    })
  }
  if (o.onAttachToSnag) {
    const attach = o.onAttachToSnag
    actions.push({ key: 'attach', label: t('Photos_attach_to_snag'), icon: Link2, primary: false, onSelect: () => attach(selectedIds) })
  }
  if (ui.canDelete) {
    actions.push({ key: 'delete', label: t('Common_delete'), icon: Trash2, primary: true, destructive: true, disabled: loading, onSelect: () => setDeleteOpen(true) })
  }

  const dialogs = (
    <>
      {editOpen && <BulkEditDialog projectId={projectId} photos={photos} ui={ui} open={editOpen} onOpenChange={setEditOpen} onDone={onClear} />}
      <DeletePhotosDialog
        projectId={projectId}
        photoIds={selectedIds}
        inUseCount={photos.filter((p) => p.inUse).length}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onDeleted={onClear}
      />
    </>
  )

  return { actions, loading, dialogs }
}
